<template>  <!-- Conditional rendering based on screen size using Quasar's platform detection -->  <component 
    :is="$q.platform.is.mobile ? HomeMobile : HomeDesktop"
    :featured-stores="featuredStores"
    :categories="categories"
    :featured-coupons="featuredCoupons"
    :latest-coupons="latestCoupons"
    :main-banners="mainBanners"
    :side-banner="sideBanner"
    :loading="loading"
    :banners-loading="loadingBanners"
    :banners-error="bannerError"
    :error="error"
    v-model:search-query="searchQuery"
    v-model:email="email"
    @subscribe="handleSubscribe"
    @refresh="handleRefresh"
  />
</template>

<script setup>
/**
 * Home.vue - Main landing page component
 * 
 * This component conditionally renders either the mobile or desktop view
 * based on the detected screen size. It handles data fetching for all shared
 * elements including featured stores, categories, coupons, and deals.
 */
import { ref, onMounted, onErrorCaptured } from "vue";
import { useRouter } from "vue-router";
import { useQuasar, useDialogPluginComponent, useMeta } from "quasar";
import { useCouponStore } from "@/stores/index";
import HomeMobile from "./HomeMobile.vue";
import HomeDesktop from "./HomeDesktop.vue";

// Access Quasar platform info for responsive rendering decisions
const $q = useQuasar();
const router = useRouter();
const couponStore = useCouponStore();

// SEO & Social Meta Tags
useMeta({
  title: 'Best Coupons & Deals in India | CouponLa',
  meta: [
    { name: 'description', content: 'Discover the latest coupons, promo codes, and deals for top Indian stores. Save big every day with CouponLa.' },
    { property: 'og:title', content: 'Best Coupons & Deals in India | CouponLa' },
    { property: 'og:description', content: 'Discover the latest coupons, promo codes, and deals for top Indian stores. Save big every day with CouponLa.' },
    { property: 'og:type', content: 'website' },
    { property: 'og:image', content: '/images/logo.svg' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: 'Best Coupons & Deals in India | CouponLa' },
    { name: 'twitter:description', content: 'Discover the latest coupons, promo codes, and deals for top Indian stores. Save big every day with CouponLa.' },
    { name: 'twitter:image', content: '/images/logo.svg' }
  ]
});

// Reactive state
const searchQuery = ref("");
const email = ref("");
const loading = ref(true);
const error = ref(null);
const featuredStores = ref([]);
const categories = ref([]);
const featuredCoupons = ref([]);
const latestCoupons = ref([]);
const mainBanners = ref([]);
const sideBanner = ref(null); // Will contain the first side banner from the array (for backward compatibility)
const loadingBanners = ref(true);
const bannerError = ref(null);

/**
 * Handles newsletter subscription
 * Shows success notification on completion
 */
const handleSubscribe = async () => {
  if (!email.value || !email.value.trim()) {
    return;
  }
  
  try {
    // Here you would integrate with your newsletter service
    await couponStore.subscribeToNewsletter(email.value);
    
    $q.notify({
      type: 'positive',
      message: 'Thanks for subscribing!',
      position: 'top',
      timeout: 2000
    });
    
    email.value = '';
  } catch (err) {
    console.error("Newsletter subscription error:", err);
    $q.notify({
      type: 'negative',
      message: 'Subscription failed. Please try again.',
      position: 'top',
      timeout: 2000
    });
  }
};

/**
 * Fetches all data needed for the homepage
 */
const fetchHomeData = async () => {
  try {
    loading.value = true;
    loadingBanners.value = true;
    error.value = null;
    bannerError.value = null;
    
    // Fetch data in parallel for better performance
    await Promise.all([
      couponStore.fetchStores(),
      couponStore.fetchCategories(),
      couponStore.fetchCoupons(),
      fetchBanners()
    ]);
    // Process fetched data with detailed logging
    console.log("Stores loaded:", couponStore.stores?.length || 0);
    console.log("Categories loaded:", couponStore.categories?.length || 0);
    console.log("Coupons loaded:", couponStore.coupons?.length || 0);
    console.log("Featured coupons:", couponStore.featuredCoupons?.length || 0);
    console.log("Main banners loaded:", mainBanners.value?.length || 0);
    console.log("Side banner loaded:", sideBanner.value ? "Yes" : "No");
    
    // Filter and transform featured stores data
    featuredStores.value = (couponStore.stores || [])
      .filter(s => s && s.is_featured)
      .map(s => ({
        ...s,
        logo_url: s.logo_url || '/images/placeholder.png' // Ensure logo_url is always available
      }))
      .slice(0, 6);
    
    // Transform categories data
    categories.value = (couponStore.categories || []).map(category => ({
      ...category,
      // Add random pastel colors for categories if color is not set
      color: category.color || generatePastelColor()
    }));
    
    // Get popular store IDs for filtering coupons
    const popularStoreIds = featuredStores.value.map(store => store.id);
    
    // Transform featured coupons and add store information
    featuredCoupons.value = (couponStore.featuredCoupons || [])
      .map(coupon => {
        // Find the related store if we have it
        const store = couponStore.stores?.find(s => s.id === coupon.store_id);
        return {
          ...coupon,
          store: store || null,
          isFromPopularStore: popularStoreIds.includes(coupon.store_id)
        };
      })
      .slice(0, 6);
    
    // Transform latest coupons and add store information
    latestCoupons.value = (couponStore.coupons || [])
      .map(coupon => {
        // Find the related store if we have it
        const store = couponStore.stores?.find(s => s.id === coupon.store_id);
        return {
          ...coupon,
          store: store || null,
          isFromPopularStore: popularStoreIds.includes(coupon.store_id)
        };
      })
      .slice(0, 6);
      
    // Make sure each array has at least a few coupons from popular stores
    ensurePopularStoreCoupons();
    
  } catch (err) {
    console.error("Error loading homepage data:", err);
    error.value = err.message || "Failed to load homepage data.";
    
    // Show offline notification if appropriate
    if (!navigator.onLine) {
      $q.notify({
        message: 'You appear to be offline. Showing cached data if available.',
        color: 'warning',
        icon: 'cloud_off',
        position: 'top',
        timeout: 3000
      });
    }
  } finally {
    loading.value = false;
    loadingBanners.value = false;
  }
};

/**
 * Fetch banner ads from Supabase
 */
const fetchBanners = async () => {
  try {
    // Import bannerService dynamically to avoid circular dependencies
    const { bannerService } = await import("@/services/supabase");
    
    // Fetch main slider banners and side banner in parallel
    const [mainBannersResult, sideBannerResult] = await Promise.all([
      bannerService.getMainSliderBanners(),
      bannerService.getSideBanner()
    ]);
    
    if (mainBannersResult.error) throw mainBannersResult.error;
    if (sideBannerResult.error) throw sideBannerResult.error;
      mainBanners.value = mainBannersResult.data || [];
      // Use all side banners from the array for display
    sideBanner.value = sideBannerResult.data || [];
    
    console.log(`Fetched ${mainBanners.value.length} main banners and ${sideBanner.value.length} side banners`);
  } catch (err) {
    console.error("Error fetching banner ads:", err);
    bannerError.value = "Failed to load banner ads.";
  }
};

/**
 * Ensures each coupon array has some items from popular stores
 * This improves display of relevant coupons
 */
const ensurePopularStoreCoupons = () => {
  const popularStoreIds = featuredStores.value.map(store => store.id);
  
  // Check if we have enough coupons from popular stores in featured coupons
  const popularCouponsInFeatured = featuredCoupons.value.filter(c => c.isFromPopularStore === true);
  
  console.log(`Initial popular stores coupons in featured: ${popularCouponsInFeatured.length}`);
  
  // If we don't have enough featured coupons from popular stores, add some from latest coupons
  if (popularCouponsInFeatured.length < 2) {
    console.log("Not enough popular store coupons, adding more from latest coupons");
    
    const popularCouponsInLatest = latestCoupons.value.filter(c => 
      popularStoreIds.includes(c.store_id) && 
      !featuredCoupons.value.some(fc => fc.id === c.id)
    );
      // Add isFromPopularStore flag to any new coupons we're adding
    const popularCouponsToAdd = popularCouponsInLatest.map(coupon => ({
      ...coupon,
      isFromPopularStore: true
    }));
    
    console.log(`Found ${popularCouponsToAdd.length} additional coupons from popular stores`);
    
    // Add more popular store coupons to featured while keeping total length limited
    featuredCoupons.value = [
      ...featuredCoupons.value,
      ...popularCouponsToAdd
    ].slice(0, 6);
    
    console.log(`Updated featured coupons count: ${featuredCoupons.value.length}`);
    console.log(`From popular stores: ${featuredCoupons.value.filter(c => c.isFromPopularStore).length}`);
  }
};

/**
 * Generates a random pastel color for category backgrounds
 */
const generatePastelColor = () => {
  const baseColors = [
    '#4285f4', '#ea4335', '#fbbc05', '#34a853', // Google-ish colors
    '#1976d2', '#e91e63', '#ff9800', '#4caf50', // Material-ish colors
    '#9c27b0', '#2196f3', '#ff5722', '#673ab7'  // More vibrant options
  ];
  
  // Pick random base color and add transparency
  const baseColor = baseColors[Math.floor(Math.random() * baseColors.length)];
  return baseColor + '99'; // Add 60% opacity
};

// Allow child components to trigger a refresh
const handleRefresh = () => fetchHomeData();

// Global error handler
onErrorCaptured((err) => {
  console.error("Captured error in Home.vue:", err);
  error.value = "Something went wrong. Please try refreshing the page.";
  return false; // Prevent error from propagating further
});

// Fetch data when component mounts
onMounted(fetchHomeData);
</script>

<style lang="scss" scoped>
// Component-level styles (shared between mobile and desktop)
:deep(.q-page) {
  min-height: calc(100vh - 50px);
  background-color: #f5f7fa;
}

:deep(.q-img) {
  background-color: #f0f0f0;
}

// Animation for skeleton loaders
@keyframes pulse {
  0% { opacity: 0.6; }
  50% { opacity: 1; }
  100% { opacity: 0.6; }
}

:deep(.q-skeleton) {
  animation: pulse 1.5s infinite;
}
</style>
